import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Check, Trash2, AlertTriangle, Clock, RefreshCw } from "lucide-react";
import { apiService } from "../services/api";
import { authService } from "../services/auth";
import { Poll } from "../types";
import { getCategoryIcon } from "../constants/categories";
import { Avatar } from "../components/radix/Avatar";
import "./AdminPage.css";

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [flaggedPolls, setFlaggedPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [actioningPollId, setActioningPollId] = useState<string | null>(null);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate("/");
      return;
    }
    if (!authService.isAdmin()) {
      navigate("/feed");
    }
  }, [navigate]);

  const fetchFlagged = async () => {
    try {
      setLoading(true);
      setError(null);
      const polls = await apiService.listFlaggedPolls();
      setFlaggedPolls(polls);
    } catch (err: any) {
      console.error("Failed to load flagged polls:", err);
      setError(err.message || "Failed to load flagged polls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authService.isAdmin()) {
      fetchFlagged();
    }
  }, []);

  const handleApprove = async (pollId: string) => {
    if (actioningPollId) return;
    setActioningPollId(pollId);
    try {
      await apiService.approveFlaggedPoll(pollId);
      // Animatively remove poll from local state list
      setFlaggedPolls((prev) => prev.filter((p) => p.pollId !== pollId));
    } catch (err: any) {
      alert(err.message || "Failed to approve poll");
    } finally {
      setActioningPollId(null);
    }
  };

  const handleDelete = async (pollId: string) => {
    if (actioningPollId) return;
    if (!window.confirm("Are you sure you want to permanently delete this poll? This cannot be undone.")) return;
    setActioningPollId(pollId);
    try {
      await apiService.deletePoll(pollId);
      setFlaggedPolls((prev) => prev.filter((p) => p.pollId !== pollId));
    } catch (err: any) {
      alert(err.message || "Failed to delete poll");
    } finally {
      setActioningPollId(null);
    }
  };

  const getReasonLabel = (reason?: string) => {
    if (!reason) return "Community Reports";
    if (reason === "AI_SAFETY_BLOCK") return "Blocked by Automated Safety AI";
    if (reason === "COMMUNITY_REPORTS") return "Community Flagged (>= 3 Reports)";
    return reason;
  };

  // Stats calculation
  const totalFlaggedCount = flaggedPolls.length;
  const highUrgencyCount = flaggedPolls.filter((p) => (p.reportsCount || 0) >= 5).length;
  const aiFlaggedCount = flaggedPolls.filter((p) => p.moderationReason === "AI_SAFETY_BLOCK").length;

  if (!authService.isAdmin()) {
    return null;
  }

  return (
    <div className="admin-page-container">
        <div className="admin-header-section">
          <div className="admin-title-wrapper">
            <div className="admin-shield-icon">
              <Shield size={24} />
            </div>
            <div>
              <h2>Moderation Control Center</h2>
              <p>Review flagged content, approve community restorations, or permanently remove guidelines violations.</p>
            </div>
          </div>
          <button className="refresh-btn" onClick={fetchFlagged} disabled={loading} title="Refresh Flagged Feed">
            <RefreshCw size={18} className={loading ? "spinning" : ""} />
          </button>
        </div>

        {/* Stat Cards Row */}
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <span className="stat-label">Under Review</span>
            <span className="stat-value">{totalFlaggedCount}</span>
            <span className="stat-desc">Flagged polls requiring decision</span>
          </div>
          <div className="admin-stat-card stat-urgent">
            <span className="stat-label">High Urgency</span>
            <span className="stat-value">{highUrgencyCount}</span>
            <span className="stat-desc">Polls with 5+ community reports</span>
          </div>
          <div className="admin-stat-card stat-ai">
            <span className="stat-label">AI Filtered</span>
            <span className="stat-value">{aiFlaggedCount}</span>
            <span className="stat-desc">Flagged automatically during creation</span>
          </div>
        </div>

        {error && <div className="admin-error-box">{error}</div>}

        <div className="moderation-feed">
          {loading ? (
            <div className="moderation-loading-state">
              <div className="moderation-spinner"></div>
              <p>Fetching flagged submissions...</p>
            </div>
          ) : flaggedPolls.length === 0 ? (
            <div className="moderation-empty-state">
              <Check size={48} className="empty-icon" />
              <h3>The queue is clean!</h3>
              <p>All reported polls have been reviewed. Thank you for maintaining community safety!</p>
            </div>
          ) : (
            <div className="flagged-cards-list">
              {flaggedPolls.map((poll) => {
                const isActioningThis = actioningPollId === poll.pollId;
                const reportsCount = poll.reportsCount || 0;
                const isUrgent = reportsCount >= 5;

                return (
                  <div
                    key={poll.pollId}
                    className={`flagged-card ${isActioningThis ? "flagged-card-acting" : ""} ${isUrgent ? "card-urgent" : ""}`}
                  >
                    {/* Flagged Status Banner */}
                    <div className="flagged-banner">
                      <div className="banner-reason">
                        <AlertTriangle size={15} />
                        <span>{getReasonLabel(poll.moderationReason)}</span>
                      </div>
                      <div className="banner-stats">
                        <span className="report-count-badge">
                          {reportsCount} {reportsCount === 1 ? "Report" : "Reports"}
                        </span>
                        {poll.flaggedAt && (
                          <span className="flagged-time">
                            <Clock size={12} />
                            {new Date(poll.flaggedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flagged-card-body">
                      {/* Original Creator Row */}
                      <div className="flagged-creator-row">
                        <Avatar
                          fallback={poll.creatorName?.slice(0, 2).toUpperCase() || "AN"}
                          src={poll.creatorImageUrl}
                          className="flagged-creator-avatar"
                        />
                        <div className="flagged-creator-info">
                          <span className="flagged-creator-name">{poll.creatorName || "Anonymous"}</span>
                          <span className="flagged-creator-id">ID: {poll.creatorId}</span>
                        </div>
                        <span className="flagged-category">
                          {getCategoryIcon(poll.category)} {poll.category}
                        </span>
                      </div>

                      {/* Content Preview */}
                      <div className="flagged-content">
                        <h4 className="flagged-question">{poll.question}</h4>
                        <div className="flagged-options-preview">
                          {poll.options.map((option) => (
                            <div key={option.optionId} className="flagged-option-row">
                              <span className="option-text">{option.text}</span>
                              <span className="option-votes">{option.voteCount} votes</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Mod Actions Area */}
                      <div className="flagged-actions">
                        <button
                          className="btn-mod-approve"
                          onClick={() => handleApprove(poll.pollId)}
                          disabled={!!actioningPollId}
                          title="Restore poll to active feeds"
                        >
                          <Check size={16} />
                          <span>Approve & Restore</span>
                        </button>
                        <button
                          className="btn-mod-delete"
                          onClick={() => handleDelete(poll.pollId)}
                          disabled={!!actioningPollId}
                          title="Permanently delete poll"
                        >
                          <Trash2 size={16} />
                          <span>Delete Permanently</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
  );
};
